import classNames from "classnames"
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { NavLink } from "react-router-dom"
import { sidebarSections } from "../../constants/routes"
import useAutoClose from "../../hooks/useAutoClose"
import Icon from "../Icon"
import SidebarSection from "./SidebarSection"

export type SidebarRef = {
  toggle(): void
  open(): void
  close(): void
}

const Sidebar = forwardRef<SidebarRef>((_props, ref) => {
  const [expanded, setExpanded] = useState(localStorage.getItem("sidebar-expanded") === "true")
  const [open, setOpen] = useState(false)

  const trigger = useRef<HTMLButtonElement>(null)
  const sidebar = useRef<HTMLDivElement>(null)

  useAutoClose({
    condition: open,
    action: () => setOpen(false),
    excludeTargets: [trigger.current, sidebar.current]
  })

  useImperativeHandle(ref, () => ({
    toggle: (): void => {
      setOpen(!open)
    },
    open: (): void => {
      setOpen(true)
    },
    close: (): void => {
      setOpen(false)
    }
  }))

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", `${expanded}`)
    document.body.classList.toggle("sidebar-expanded", expanded)
  }, [expanded])

  return (
    <>
      <div className={classNames(
        "sidebar__backdrop",
        open && "sidebar__backdrop--open"
      )} aria-hidden="true"></div>

      <div
        ref={sidebar}
        className={classNames(
          "sidebar",
          open && "sidebar--open"
        )}
      >

        <div className="sidebar__header">
          <button
            ref={trigger}
            className="sidebar__close"
            onClick={(): void => setOpen(!open)}
            aria-controls="sidebar"
            aria-expanded={open}
          >
            <span className="sr-only">Close sidebar</span>
            <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
            </svg>
          </button>

          <NavLink to="/" className="sidebar__logo">
            <Icon name="logo" size={30} />
          </NavLink>
        </div>

        <div className="sidebar__sections">
          {sidebarSections.map((section, i) => (
            <SidebarSection
              key={i}
              section={section}
              expanded={expanded}
              setExpanded={setExpanded}
            />
          ))}
        </div>

        <div className="sidebar__expand">
          <div className="sidebar__expand-wrapper">
            <button onClick={(): void => setExpanded(!expanded)}>
              <span className="sr-only">Expand / collapse sidebar</span>

              <svg className="sidebar__expand-icon" viewBox="0 0 24 24">
                <path className="text-gray-400" d="M19.586 11l-5-5L16 4.586 23.414 12 16 19.414 14.586 18l5-5H7v-2z" />
                <path className="text-gray-600" d="M3 23H1V1h2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  )
})

export default Sidebar
